import { type Path } from 'react-router';
import { useAccountContext } from '@gonfalon/app-context/useAccountContext';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { toPlanUsageDetails } from '@gonfalon/navigator/navigators';
import { getSubscriptionUsageQuery } from '@gonfalon/rest-api/queries/billing';
import { ProgressBar } from '@launchpad-ui/components';
import { useSuspenseQuery } from '@tanstack/react-query';

import { AggregationType, LimitNames } from '../../../types';
import { useAllUsageTabData } from '../../hooks/useAllUsageTabData';
import { useVisibleUsageTabs } from '../../hooks/useVisibleUsageTabs';
import { getSubscriptionLimits } from '../../utils/getSubscriptionLimits';

import { RemainingUsageCard, TotalUsageCard } from './components';

import styles from '../common/UsageOverview.module.css';

type PlanUsageOverviewProps = {
  detailsRedirectFn?: ({ metric }: { metric: string }) => Path;
};

export function PlanUsageOverview({ detailsRedirectFn }: PlanUsageOverviewProps = {}) {
  const { subscription } = useAccountContext();

  // Wait for subscription usage to be loaded
  const { data: subscriptionUsageRep } = useSuspenseQuery(getSubscriptionUsageQuery());

  const limits = getSubscriptionLimits(subscription);
  const { tabs: visibleTabs, isPending } = useVisibleUsageTabs();

  const aggregationType = AggregationType.MONTH_TO_DATE;
  const allUsageData = useAllUsageTabData(visibleTabs, aggregationType);

  const subscriptionUsage = subscriptionUsageRep._usage;
  const seatsUsage: number = (subscriptionUsage?.[LimitNames.SEATS] ?? 0) as number;
  const seatsLimit: number = limits?.[LimitNames.SEATS] ?? 0;

  return (
    <div>
      <h2 className={styles.overviewTitle}>
        <Message id="billing.planUsageOverview.overview" />
      </h2>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerTitle}>
            <h3>
              <Message id="billing.planUsageOverview.planUsage" />
            </h3>
          </div>
          <div className={styles.headerSubText}>
            <Message id="billing.planUsageOverview.monthlyUsageResets" />
          </div>
        </div>
      </div>
      {isPending ? (
        <ProgressBar isIndeterminate size="large" aria-label={msg('billing.planUsagePage.loadingLabel')} />
      ) : (
        <div className={styles.cardGrid}>
          <div className={styles.cardLeftColumn}>
            <TotalUsageCard title={msg('billing.planUsageOverview.seats')} value={seatsUsage} limit={seatsLimit} />
            <RemainingUsageCard seatsUsage={seatsUsage} seatsLimit={seatsLimit} allUsageData={allUsageData} />
          </div>
          {allUsageData?.map((usageData) => {
            const { tab, title, limit, total, precision } = usageData;
            return (
              <TotalUsageCard
                key={tab}
                title={title}
                value={total}
                limit={limit}
                aggregationType={aggregationType}
                usageData={usageData}
                precision={precision}
                detailsRedirectFn={detailsRedirectFn || toPlanUsageDetails}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
